import React from "react";
import { cn } from "@/lib/utils";

export interface SkriftMarkProps {
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

// Brand mark only (without the wordmark). Sizes align with SkriftLogo heights.
export function SkriftMark({ className, size = "md" }: SkriftMarkProps) {
  const sizeMap = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-10 w-10",
    xl: "h-12 w-12",
  } as const;

  return (
    <div
      className={cn(
        "inline-flex shrink-0 items-center",
        sizeMap[size],
        className
      )}
    >
      <svg
        className="h-full w-full"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <g clipPath="url(#clip0_123_1094)">
          <mask
            id="mask0_123_1094"
            style={{ maskType: "alpha" } as React.CSSProperties}
            maskUnits="userSpaceOnUse"
            x="0"
            y="7"
            width="12"
            height="11"
          >
            <path
              d="M1.21353 7.78646C1.87512 7.12487 2.94778 7.12487 3.60937 7.78646C4.27097 8.44806 4.27097 9.52071 3.60937 10.1823C2.94778 10.8439 1.87512 10.8439 1.21353 10.1823C0.551932 9.52071 0.551932 8.44806 1.21353 7.78646Z"
              fill="#5A4EFD"
            />
            <path
              d="M4.20821 10.7813C3.54662 11.4428 3.54662 12.5155 4.20821 13.1771L7.59058 16.5595C8.36893 17.3378 9.63088 17.3378 10.4092 16.5595L10.9679 16.0007C11.2042 15.7645 11.2042 15.3814 10.9679 15.1451L6.11437 10.2916C5.40776 9.58499 5.4 9.58946 4.20821 10.7813Z"
              fill="#5A4EFD"
            />
          </mask>
          <g mask="url(#mask0_123_1094)">
            <rect
              x="-6.75012"
              y="1.44"
              width="18.81"
              height="18.81"
              fill="url(#paint0_linear_123_1094)"
            />
          </g>
          <mask
            id="mask1_123_1094"
            style={{ maskType: "alpha" } as React.CSSProperties}
            maskUnits="userSpaceOnUse"
            x="3"
            y="3"
            width="12"
            height="12"
          >
            <path
              d="M6.00517 3.59376L14.3906 11.9792C14.556 12.1446 14.556 12.4128 14.3906 12.5782L12.5937 14.3751C12.4283 14.5405 12.1602 14.5405 11.9948 14.3751L3.60932 5.9896C3.44393 5.82421 3.44393 5.55604 3.60932 5.39064L5.40621 3.59376C5.57161 3.42836 5.83977 3.42836 6.00517 3.59376Z"
              fill="#5A4EFD"
            />
          </mask>
          <g mask="url(#mask1_123_1094)">
            <rect
              x="3.14998"
              y="3.24001"
              width="11.88"
              height="11.88"
              fill="url(#paint1_linear_123_1094)"
            />
          </g>
          <mask
            id="mask2_123_1094"
            style={{ maskType: "alpha" } as React.CSSProperties}
            maskUnits="userSpaceOnUse"
            x="6"
            y="0"
            width="12"
            height="11"
          >
            <path
              d="M13.7917 7.18747C14.4532 6.52588 14.4532 5.45322 13.7917 4.79163L10.4093 1.40926C9.63094 0.630913 8.36899 0.630913 7.59065 1.40926L7.03195 1.96795C6.79566 2.20424 6.79566 2.58733 7.03195 2.82361L11.3958 7.18747C12.6 8.39168 12.6 8.37918 13.7917 7.18747Z"
              fill="#5A4EFD"
            />
            <path
              d="M14.3906 7.78646C15.0522 7.12487 16.1249 7.12487 16.7865 7.78646C17.4481 8.44806 17.4481 9.52071 16.7865 10.1823C16.1249 10.8439 15.0522 10.8439 14.3906 10.1823C13.729 9.52071 13.729 8.44806 14.3906 7.78646Z"
              fill="#5A4EFD"
            />
          </mask>
          <g mask="url(#mask2_123_1094)">
            <rect
              x="6.1199"
              y="-0.450012"
              width="11.88"
              height="11.88"
              fill="url(#paint2_linear_123_1094)"
            />
          </g>
        </g>
        <defs>
          <linearGradient
            id="paint0_linear_123_1094"
            x1="12.0516"
            y1="20.2471"
            x2="-6.75281"
            y2="1.44269"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FFB372" />
            <stop offset="1" stopColor="#EC270A" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_123_1094"
            x1="15.03"
            y1="15.12"
            x2="3.14998"
            y2="3.24001"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#7778FF" />
            <stop offset="1" stopColor="#5438F3" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_123_1094"
            x1="6.1199"
            y1="-0.450012"
            x2="17.9999"
            y2="11.43"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#ED115B" />
            <stop offset="1" stopColor="#FF6991" />
          </linearGradient>
          <clipPath id="clip0_123_1094">
            <rect width="18" height="18" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}
